import {
  TextField as MuiTextField,
  Stack as MuiStack,
  FormControl as MuiFormControl,
  InputLabel as MuiInputLabel,
  Select as MuiSelect,
  MenuItem as MuiMenuItem,
  Switch as MuiSwitch,
  Box as MuiBox,
} from "@mui/material";
import PropTypes from "prop-types";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import SidebarSectionHeader from "./SidebarSectionHeader";
import { useTranslation } from "react-i18next";
import { StudyListingPageStyle } from "./StudyListingPageStyle";
import axios from "axios";
import { GET_STUDIES_URL_BASE } from "app/shared/constants";
import { useCallback, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useTheme } from "@mui/material/styles";

const StudyContactInfoSection = ({
  studyId,
  studyContacts = [],
  otherContactEmail,
  otherContactEmailVisibility,
  otherContactPhone,
  otherContactPhoneVisibility,
  mainPointOfContact = false,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  // const [error, setError] = useState(null);

  const theme = useTheme();
  const styles = StudyListingPageStyle(theme);

  const OTHER_CONTACT_ID = "OTHER";
  const otherContact = useMemo(() => {
    const obj = {
      id: OTHER_CONTACT_ID,
      firstName: "Other",
      lastName: "",
      email: otherContactEmail || "",
      phone: otherContactPhone || "",
      emailVisibility: !!otherContactEmailVisibility,
      phoneVisibility: !!otherContactPhoneVisibility,
      isOtherContact: true,
      mainPointOfContact: !!mainPointOfContact,
    };
    return obj;
  }, [
    otherContactEmail,
    otherContactEmailVisibility,
    otherContactPhone,
    otherContactPhoneVisibility,
    mainPointOfContact,
  ]);

  const sortedStudyContacts = useMemo(() => {
    if (!studyContacts?.length) return [];

    const main = studyContacts.find((c) => c.mainPointOfContact);
    if (main) {
      return [
        main,
        ...studyContacts.filter((c) => c.id !== main.id),
      ];
    }

    const lead = studyContacts.find((c) => c.leadCrc);
    if (lead) {
      return [
        lead,
        ...studyContacts.filter((c) => c.id !== lead.id),
      ];
    }

    return studyContacts;
  }, [studyContacts]);

  const determineSelectedContactId = useCallback(() => {
    const mainContact = studyContacts.find((c) => c.mainPointOfContact);
    if (mainContact) return mainContact.id;

    if (otherContact && otherContact.mainPointOfContact) {
      return OTHER_CONTACT_ID;
    }

    const leadCrcContact = studyContacts.find((c) => c.leadCrc);
    if (leadCrcContact) return leadCrcContact.id;

    if (otherContactEmail || otherContactPhone) return OTHER_CONTACT_ID;

    return studyContacts.length > 0
      ? studyContacts[0].id
      : OTHER_CONTACT_ID;
  }, [studyContacts, otherContact, otherContactEmail, otherContactPhone]);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: {
      contacts: [...sortedStudyContacts, otherContact],
      selectedContactId: determineSelectedContactId(),
    },
    validate: (values) => {
      const errors = {};

      const selected = values.contacts.find(
        (c) => c.id === values.selectedContactId
      );

      const isOtherSelected = selected?.isOtherContact;
      const isVisible = !!selected?.emailVisibility;
      const email = selected?.email?.trim() || "";

      if (isOtherSelected && isVisible) {
        if (!email) {
          errors.email = "Email is required";
        } else {
          const emailRegex =
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

          if (!emailRegex.test(email)) {
            errors.email = "Enter a valid email address";
          }
        }
      }

      return errors;
    },
    onSubmit: () => {
      if (Object.keys(formik.errors).length > 0) return;
      saveContactsAPI();
    },
  });

  const saveContactsAPI = async () => {
    if (!studyId) return;
    setLoading(true);
    try {
      if (formik.values.contacts.length === 0)
        throw new Error("No contacts to save");

      const payload = ((contacts) => {
        const other = contacts.find((c) => c.isOtherContact);
        const selectedId = formik.values.selectedContactId;
        // const leadCrcId =
        //   formik.values.selectedContactId === OTHER_CONTACT_ID
        //     ? null
        //     : formik.values.selectedContactId;
        return {
          studyContacts: contacts
            .filter((c) => !c.isOtherContact)
            .map((c) => ({
              id: c.id,
              //leadCrc: leadCrcId === c.id,
              email: c.email,
              phone: c.phone,
              emailVisibility: c.emailVisibility,
              phoneVisibility: c.phoneVisibility,
              mainPointOfContact: selectedId === c.id,
            })),
          recruitmentEmail: other?.email || null,
          recruitmentPhone: other?.phone || null,
          emailVisibility: !!other?.emailVisibility,
          phoneVisibility: !!other?.phoneVisibility,
          mainPointOfContact: selectedId === OTHER_CONTACT_ID,
        };
      })(formik.values.contacts);

      await axios.patch(
        `${GET_STUDIES_URL_BASE}/study-website-content/${studyId}`,
        payload,
        { headers: { "Content-Type": "application/merge-patch+json" } },
      );
    } catch (err) {
      console.error("Failed to save contacts", err);
    }
    setLoading(false);
  };

  const updateContactField = (field, value, contactId) => {
    const updatedContacts = formik.values.contacts.map((contact) =>
      contact.id === contactId ? { ...contact, [field]: value } : contact,
    );
    formik.setFieldValue("contacts", updatedContacts);
  };

  const toggleVisibility = (field, contactId) => {
    const contactIndex = formik.values.contacts.findIndex(
      (c) => c.id === contactId
    );

    const currentValue =
      formik.values.contacts[contactIndex][field];

    formik.setFieldValue(
      `contacts[${contactIndex}].${field}`,
      !currentValue,
      true
    );
  };

  const selectedContact = formik.values.contacts.find(
    (c) => c.id === formik.values.selectedContactId,
  );
  const isOtherSelected = selectedContact?.isOtherContact;

  return (
    <MuiBox sx={styles.sideBarSection}>
      <SidebarSectionHeader
        title={t("StudyWebisteContentForm.studyContactInfo")}
        description={t("StudyWebisteContentForm.studyContactDescription")}
        helpText={
          "The contacts listed are obtained from the study record in OnCore."
        }
        loading={loading}
      />

      {/* Contact dropdown */}
      <MuiFormControl fullWidth margin="normal">
        <MuiInputLabel>
          {t("StudyWebisteContentForm.pointOfContact")}
        </MuiInputLabel>
        <MuiSelect
          label={t("StudyWebisteContentForm.pointOfContact")}
          value={formik.values.selectedContactId || ""}
          onChange={(e) => {
            const newContactId = e.target.value;
            formik.setFieldValue("selectedContactId", newContactId);
            formik.submitForm();
          }}
          size="small"
          disabled={loading}
        >
          {formik.values.contacts.map((contact) => (
            <MuiMenuItem key={contact.id} value={contact.id}>
              {contact.firstName} {contact.lastName}
            </MuiMenuItem>
          ))}
        </MuiSelect>
      </MuiFormControl>

      {/* Selected Contact Info */}
      {selectedContact && (
        <>
          {/* Email */}
          <MuiStack direction="row" alignItems="center" spacing={1} mt={1}>
            <VisibilityOffIcon sx={{ height: "0.7em" }} />
            <MuiSwitch
              sx={{
                paddingRight: "5px",
                marginLeft: "-8px !important",
                width: "51px",
              }}
              checked={!!selectedContact.emailVisibility}
              onChange={() => {
                toggleVisibility("emailVisibility", selectedContact.id);
                //formik.submitForm();
              }}
              disabled={loading}
            />
            <VisibilityIcon
              sx={{ height: "0.7em", marginLeft: "0 !important" }}
            />
            <MuiTextField
              variant="standard"
              size="small"
              fullWidth
              type="email"
              name="email"
              label={t("StudyWebisteContentForm.emailAddress")}
              value={selectedContact.email || ""}
              onChange={(e) => {
                updateContactField("email", e.target.value, selectedContact.id);
              }}
              disabled={
                loading || !isOtherSelected || !selectedContact.emailVisibility
              }
              onBlur={(e) => {
                formik.handleBlur(e);
                formik.submitForm();
              }}
              error={
                isOtherSelected && Boolean(formik.errors.email)
              }
              helperText={
                isOtherSelected && formik.errors.email ? formik.errors.email : ""
              }
            />
          </MuiStack>

          {/* Phone */}
          <MuiStack direction="row" alignItems="center" spacing={1} mt={1}>
            <VisibilityOffIcon sx={{ height: "0.7em" }} />
            <MuiSwitch
              sx={{
                paddingRight: "5px",
                marginLeft: "-8px !important",
                width: "51px",
              }}
              checked={!!selectedContact.phoneVisibility}
              onChange={() => {
                toggleVisibility("phoneVisibility", selectedContact.id);
                formik.submitForm();
              }}
              disabled={loading}
            />
            <VisibilityIcon
              sx={{ height: "0.7em", marginLeft: "0 !important" }}
            />
            <MuiTextField
              variant="standard"
              size="small"
              name="phone"
              fullWidth
              label={t("StudyWebisteContentForm.phone")}
              value={selectedContact.phone || ""}
              onChange={(e) =>
                updateContactField("phone", e.target.value, selectedContact.id)
              }
              disabled={
                loading || !isOtherSelected || !selectedContact.phoneVisibility
              }
              onBlur={(e) => {
                formik.handleBlur(e);
                formik.submitForm();
              }}
              error={Boolean(formik.touched.phone && formik.errors.phone)}
              helperText={formik.touched.phone && formik.errors.phone}
            />
          </MuiStack>
        </>
      )}
    </MuiBox>
  );
};

StudyContactInfoSection.propTypes = {
  studyId: PropTypes.number.isRequired,
  studyContacts: PropTypes.isRequired,
  otherContactEmail: PropTypes.string,
  otherContactEmailVisibility: PropTypes.bool,
  otherContactPhone: PropTypes.string,
  otherContactPhoneVisibility: PropTypes.bool,
  mainPointOfContact: PropTypes.bool,
};

export default StudyContactInfoSection;
